import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMegaphone, faQuestion } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "@mui/material";
import {
  PortalFeedbackDismissModal,
  PortalFeedbackPromptActions,
} from "@bto/shared";
import {
  FeedbackIconButton,
  FeedbackBannerContainer,
  HeaderActionDivider,
  HeaderContainer,
  HeaderContainerMobile,
  HeaderIcon,
  HeaderIconButton,
  MobileMenuToggleIcon,
  HeaderIconContainerMobileWrapper,
  HeaderIconContainerMobileGrid,
  HeaderIconContainerMobile,
  HeaderIconsContainer,
  HeaderIconsLinksContainer,
  HeaderLinksContainer,
  HeaderLockupImg,
} from "./Header.styles";
import providerPortalLockup from "../../assets/logos/provider-portal/unity-horiz-cobranded-tint.svg";
import menuExpand from "../../assets/icons/menu.svg";
import close from "../../assets/icons/close.svg";
import { MobileMenu } from "../MobileMenu/MobileMenu";
import { GetHeaderMenuLinks, GetLoginHeader } from "./HeaderMenuLinks";
import { LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { orderedRequisitions, providerState } from "../../recoil/atoms";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useProviderFeedbackPromptEligibility } from "../../utils/hooks/useProviderFeedbackPromptEligibility";
import { Banner } from "../Banner/Banner";
import { ProviderFeedbackModal } from "../Feedback/ProviderFeedbackModal";
import { DRAFT_STATUS } from "../NewRequisition/types";
import { useFeatureDiscovery } from "../FeatureDiscovery/FeatureDiscoveryProvider";

const FEEDBACK_AUTO_PROMPT_MIN_ORDERED = 5;
const PROVIDER_FEEDBACK_PROMPT_HANDLED_KEY = "providerFeedbackPromptHandled";
const FEEDBACK_TOOL_REMINDER =
  "You can find the feedback tool in the top bar anytime you feel like giving us feedback.";

const getProviderFeedbackStorageKey = (key: string, username: string) =>
  `${key}:${username}`;

interface HeaderProps {
  isHelpPanelOpen?: boolean;
  onToggleHelpPanel?: () => void;
}

export const Header = ({
  isHelpPanelOpen = false,
  onToggleHelpPanel,
}: HeaderProps): React.ReactElement => {
  const [activeHeader, setActiveHeader] = useState("");
  const [, setProviderInfo] = useRecoilState(providerState);
  const { auth } = useAuthenticationHook();
  const { registerHelpAnchor } = useFeatureDiscovery();
  const history = useHistory();
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    setActiveHeader(pathname);
  }, [pathname]);

  const setPathnameCheck = () => {
    setActiveHeader(history.location.pathname);
  };

  useEffect(() => {
    const unlisten = history.listen(() => {
      setPathnameCheck();
    });
    return () => {
      unlisten();
    };
  }, []);

  const headerLinks = useMemo(
    () =>
      auth?.token
        ? GetHeaderMenuLinks("white", "medium", activeHeader, setActiveHeader, {
            onToggleHelpPanel,
            isHelpPanelOpen,
            includeHelpLink: !onToggleHelpPanel,
          })
        : GetLoginHeader(
            "white",
            "medium",
            setProviderInfo,
            pathname === LOGIN_PAGE_URL,
          ),

    [pathname, auth?.token, activeHeader, isHelpPanelOpen, onToggleHelpPanel],
  );

  const { isDesktop, deviceType } = useDeviceHook();
  const isAuthenticated = Boolean(auth?.token);
  const [navBarOpen, setNavBarOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isFeedbackDismissModalOpen, setIsFeedbackDismissModalOpen] =
    useState(false);
  const [wasAutoPromptOpened, setWasAutoPromptOpened] = useState(false);
  const [isFeedbackTooltipOpen, setIsFeedbackTooltipOpen] = useState(false);
  const feedbackIconAnchorRef = useRef<HTMLSpanElement | null>(null);
  const allOrderedRequisitions = useRecoilValue(orderedRequisitions);
  const providerStorageId = auth?.username ?? "unknown";

  const orderedCount = useMemo(
    () =>
      allOrderedRequisitions.filter(
        (row) =>
          row.draft_status === DRAFT_STATUS.EMPTY ||
          row.draft_status === DRAFT_STATUS.SUBMITTED ||
          row.draft_status === DRAFT_STATUS.SUBMITTED_REDRAW,
      ).length,
    [allOrderedRequisitions],
  );

  const feedbackPromptHandledStorageKey = useMemo(
    () =>
      getProviderFeedbackStorageKey(
        PROVIDER_FEEDBACK_PROMPT_HANDLED_KEY,
        providerStorageId,
      ),
    [providerStorageId],
  );
  const markFeedbackPromptHandled = useCallback(() => {
    localStorage.setItem(feedbackPromptHandledStorageKey, "true");
  }, [feedbackPromptHandledStorageKey]);

  const { promptAction } = useProviderFeedbackPromptEligibility({
    authToken: auth?.token,
    feedbackPromptHandledStorageKey,
    orderedCount,
    orderedMin: FEEDBACK_AUTO_PROMPT_MIN_ORDERED,
    isDesktop,
    isFeedbackModalOpen,
  });
  const showFeedbackPromptBanner = promptAction === "show_banner";

  const toggleFeedbackModal = useCallback(() => {
    if (isFeedbackModalOpen && wasAutoPromptOpened) {
      markFeedbackPromptHandled();
    }
    setIsFeedbackModalOpen((currentIsOpen) => !currentIsOpen);
    setWasAutoPromptOpened(false);
  }, [isFeedbackModalOpen, markFeedbackPromptHandled, wasAutoPromptOpened]);

  const openFeedbackModal = useCallback(() => {
    setWasAutoPromptOpened(false);
    setIsFeedbackModalOpen(true);
  }, []);

  const openFeedbackModalFromAutoPrompt = useCallback(() => {
    setIsFeedbackModalOpen(true);
    setWasAutoPromptOpened(true);
  }, []);

  const handleGiveFeedback = useCallback(() => {
    markFeedbackPromptHandled();
    openFeedbackModalFromAutoPrompt();
  }, [markFeedbackPromptHandled, openFeedbackModalFromAutoPrompt]);

  const handleFeedbackModalClose = useCallback(() => {
    if (wasAutoPromptOpened) {
      markFeedbackPromptHandled();
    }
    setIsFeedbackModalOpen(false);
  }, [markFeedbackPromptHandled, wasAutoPromptOpened]);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      handleFeedbackModalClose();
      previousPathnameRef.current = pathname;
    }
  }, [handleFeedbackModalClose, pathname]);

  useEffect(() => {
    if (promptAction === "auto_open_modal") {
      openFeedbackModalFromAutoPrompt();
    }
  }, [openFeedbackModalFromAutoPrompt, promptAction]);

  const dismissFeedbackPromptBanner = () => {
    markFeedbackPromptHandled();
    setIsFeedbackDismissModalOpen(true);
  };

  const handleToggle = () => {
    setNavBarOpen(!navBarOpen);
  };

  const feedbackButton = (
    <FeedbackIconButton
      type="button"
      $active={isFeedbackModalOpen}
      $svgSize="14px"
      onClick={toggleFeedbackModal}
      onMouseEnter={() => setIsFeedbackTooltipOpen(true)}
      onMouseLeave={() => setIsFeedbackTooltipOpen(false)}
      onFocus={() => setIsFeedbackTooltipOpen(true)}
      onBlur={() => setIsFeedbackTooltipOpen(false)}
      aria-label="Open feedback tool"
      aria-pressed={isFeedbackModalOpen}
      data-testid="header-feedback-button"
      title="Give us feedback"
    >
      <Tooltip
        open={isFeedbackTooltipOpen}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        arrow
        title="Give us feedback"
        placement="top-start"
        PopperProps={{ anchorEl: feedbackIconAnchorRef.current }}
      >
        <span ref={feedbackIconAnchorRef}>
          <FontAwesomeIcon icon={faMegaphone} fixedWidth />
        </span>
      </Tooltip>
    </FeedbackIconButton>
  );

  const helpButton = onToggleHelpPanel && (
    <Tooltip
      title="Help & FAQ"
      placement="top-start"
      arrow
      disableTouchListener
    >
      <HeaderIconButton
        ref={registerHelpAnchor}
        type="button"
        $active={isHelpPanelOpen}
        aria-label="Toggle Help and FAQ panel"
        aria-controls="provider-help-panel"
        aria-expanded={isHelpPanelOpen}
        onClick={onToggleHelpPanel}
        data-testid="header-help-button"
      >
        <FontAwesomeIcon icon={faQuestion} fixedWidth />
      </HeaderIconButton>
    </Tooltip>
  );

  const feedbackBannerTitle =
    "Hi there, help us improve our experience by giving us some feedback";
  const feedbackBannerCTA = (
    <PortalFeedbackPromptActions
      onNotNow={dismissFeedbackPromptBanner}
      onGiveFeedback={handleGiveFeedback}
    />
  );

  return (
    <>
      {isDesktop && (
        <HeaderContainer data-testid="app-header">
          <HeaderIconsLinksContainer>
            <HeaderIconsContainer data-testid="HeaderIcons">
              <HeaderLockupImg
                src={providerPortalLockup}
                alt="Unity by BillionToOne"
              />
            </HeaderIconsContainer>
            <HeaderLinksContainer onlyLogin={!isAuthenticated}>
              {headerLinks.map((headerLink) => (
                <React.Fragment key={headerLink.key}>
                  {headerLink.header}
                </React.Fragment>
              ))}
              {isAuthenticated && <HeaderActionDivider aria-hidden="true" />}
              {isAuthenticated && helpButton}
              {isAuthenticated && feedbackButton}
            </HeaderLinksContainer>
          </HeaderIconsLinksContainer>
        </HeaderContainer>
      )}
      {!isDesktop && (
        <>
          <HeaderContainerMobile data-testid="app-header">
            <HeaderIconContainerMobile>
              <HeaderIconContainerMobileWrapper>
                <HeaderLockupImg
                  src={providerPortalLockup}
                  alt="Unity by BillionToOne"
                />
              </HeaderIconContainerMobileWrapper>
              {pathname !== LOGIN_PAGE_URL && (
                <HeaderIconContainerMobileGrid deviceType={deviceType}>
                  <MobileMenuToggleIcon
                    src={navBarOpen ? close : menuExpand}
                    width="20px"
                    onClick={handleToggle}
                    data-testid="mobile-menu-toggle"
                  />
                </HeaderIconContainerMobileGrid>
              )}
            </HeaderIconContainerMobile>
          </HeaderContainerMobile>
          {navBarOpen && (
            <MobileMenu
              handleToggle={handleToggle}
              onToggleHelpPanel={onToggleHelpPanel}
              isHelpPanelOpen={isHelpPanelOpen}
              onOpenFeedback={isAuthenticated ? openFeedbackModal : undefined}
            />
          )}
        </>
      )}
      {showFeedbackPromptBanner && !isDesktop && (
        <FeedbackBannerContainer>
          <Banner
            bannerType="information"
            bannerTitle={feedbackBannerTitle}
            bannerText=""
            bannerActions={feedbackBannerCTA}
          />
        </FeedbackBannerContainer>
      )}
      {isAuthenticated && (
        <ProviderFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={handleFeedbackModalClose}
          source={wasAutoPromptOpened ? "auto" : "manual"}
          isHelpPanelOpen={isHelpPanelOpen}
        />
      )}
      <PortalFeedbackDismissModal
        isOpen={isFeedbackDismissModalOpen}
        onClose={() => setIsFeedbackDismissModalOpen(false)}
        bodyMessage={FEEDBACK_TOOL_REMINDER}
      />
    </>
  );
};
